import { useEffect, useMemo, useState } from 'react';
import { Form } from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import ProductService from 'services/product';
import ProductVariantService from 'services/product_variant';
import PricingService from 'services/pricing';
import { useApp } from 'contexts/app';

const service = new ProductService();
const productVariantService = new ProductVariantService();
const pricingService = new PricingService();

const defaultDraftOptions = [{ id: 'default', title: 'Mặc định', values: ['default'] }];
const defaultDraftVariants = [{
  variant_id: 'default',
  variant_title: 'Mặc định',
  options: [{ id: 'default', value: 'default' }],
  image: null,
  original_price: 0,
  on_hand_quantity: 0,
  reserved_quantity: 0,
  price_list: [],
}];

const cloneDraftVariants = (variants = []) => (variants || []).map((v) => ({
  ...v,
  options: [...(v?.options || [])],
  price_list: [...(v?.price_list || [])],
}));

const cloneDraftOptions = (options = []) => (options || []).map((o) => ({
  ...o,
  values: [...(o?.values || [])],
  option_values: [...(o?.option_values || [])],
}));

const defaultValues = {
  track_inventory: false,
  allow_backorder: true,
  show_on_website: true,
  images: [],
  videos: [],
  tags: [],
};

const useEditor = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const copyId = searchParams.get('copy');
  const navigate = useNavigate();
  const { showLoading, showNotify } = useApp();
  const [form] = Form.useForm();
  const watchedImages = Form.useWatch('images', form);
  const watchedTrackInventory = Form.useWatch('track_inventory', form);
  const [data, setData] = useState(null);
  const [draftOptions, setDraftOptions] = useState(cloneDraftOptions(defaultDraftOptions));
  const [draftVariants, setDraftVariants] = useState(cloneDraftVariants(defaultDraftVariants));
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [priceEditContext, setPriceEditContext] = useState({
    selectedSourceKey: 'base',
    isTableSource: false,
    isTableManualMode: false,
    isTableAutoMode: false,
    selectedTableId: null,
  });

  const loadData = async () => {
    if (!id) {
      if (copyId) {
        try {
          showLoading(true);
          const source = await service.get(copyId);
          const copiedName = String(source?.name || '').trim();
          const nextValues = {
            ...defaultValues,
            ...source,
            id: undefined,
            name: copiedName ? `${copiedName} - Copy` : 'Sản phẩm copy',
            show_on_website: !source?.hide,
            images: source?.images || [],
            videos: source?.videos || [],
            tags: source?.tags || [],
            category: source?.category || source?.categories || [],
          };

          setData(null);
          form.resetFields();
          form.setFieldsValue(nextValues);
          setDraftOptions(cloneDraftOptions(source?.options?.length ? source.options : defaultDraftOptions));
          setDraftVariants(cloneDraftVariants(source?.variants?.length ? source.variants : defaultDraftVariants));
          return;
        } finally {
          showLoading(false);
        }
      }

      setData(null);
      form.resetFields();
      form.setFieldsValue(defaultValues);
      setDraftOptions(cloneDraftOptions(defaultDraftOptions));
      setDraftVariants(cloneDraftVariants(defaultDraftVariants));
      return;
    }

    try {
      showLoading(true);
      const item = await service.get(id);
      setData({
        ...item,
        images: item?.images || [],
        options: item?.options || [],
        variants: item?.variants || [],
      });
      setDraftOptions(cloneDraftOptions(item?.options?.length ? item.options : defaultDraftOptions));
      setDraftVariants(cloneDraftVariants(item?.variants?.length ? item.variants : defaultDraftVariants));
      form.setFieldsValue({
        ...defaultValues,
        ...item,
        show_on_website: !item?.hide,
        images: item?.images || [],
        videos: item?.videos || [],
        tags: item?.tags || [],
      });
    } finally {
      showLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id, copyId]);

  const syncVariantAndPrice = async ({ productId, options, variants, priceContext }) => {
    const optionsForSync = cloneDraftOptions(options?.length ? options : defaultDraftOptions);
    const variantsForSync = cloneDraftVariants(variants?.length ? variants : defaultDraftVariants);

    await productVariantService.update({ id: productId, options: optionsForSync, variants: variantsForSync });

    const hasPriceList = variantsForSync.some((v) => (v?.price_list || []).length > 0 || !!v?.original_price);
    const hasInventory = variantsForSync.some((v) => (Number(v?.on_hand_quantity) || 0) > 0);
    if (!hasPriceList && !hasInventory) {
      return;
    }

    const productData = await service.get(productId);
    const savedVariants = productData?.variants || [];
    const draftVariantMap = variantsForSync.reduce((acc, v) => {
      acc[v?.variant_id] = v;
      return acc;
    }, {});

    if (hasPriceList) {
      const isTableManualMode = !!priceContext?.isTableManualMode;
      const tableId = priceContext?.selectedTableId;

      if (isTableManualMode && tableId) {
        const items = savedVariants.map((v) => {
          const draft = draftVariantMap[v?.variant_id] || draftVariantMap[v?.id];
          return {
            variant_id: v?.id,
            product_id: v?.product_id,
            original_price: Number(draft?.original_price ?? v?.original_price ?? 0) || 0,
            price_list: [...(draft?.price_list || [])],
          };
        });
        await pricingService.savePriceItems({ id: tableId, items });
      } else {
        const variantsForPriceSync = savedVariants.map((v) => {
          const draft = draftVariantMap[v?.variant_id] || draftVariantMap[v?.id];
          return {
            ...v,
            price_list: [...(draft?.price_list || [])],
          };
        });
        await productVariantService.updatePriceList({ variants: variantsForPriceSync });
      }
    }

    if (hasInventory) {
      const variantsForInventorySync = savedVariants.map((v) => {
        const draft = draftVariantMap[v?.variant_id];
        return { id: v.id, on_hand_quantity: Number(draft?.on_hand_quantity) || 0 };
      });
      await productVariantService.bulkUpdateInventory({ variants: variantsForInventorySync });
    }
  };

  const onFinish = async (values) => {
    const { show_on_website = true, ...payload } = values;
    try {
      setSaving(true);
      if (id) {
        await service.updateGeneralInfo({ ...payload, id });
        const shouldShowOnWebsite = !!show_on_website;
        const isCurrentlyShown = !data?.hide;
        if (shouldShowOnWebsite !== isCurrentlyShown) {
          if (shouldShowOnWebsite) {
            await service.unhide(id);
          } else {
            await service.hide(id);
          }
        }
        try {
          await syncVariantAndPrice({
            productId: id,
            options: draftOptions,
            variants: draftVariants,
            priceContext: priceEditContext,
          });
        } catch (syncError) {
          showNotify('Đã lưu thông tin sản phẩm nhưng chưa đồng bộ xong biến thể/bảng giá, vui lòng kiểm tra lại.');
        }
        showNotify('Đã cập nhật sản phẩm');
        await loadData();
        return;
      }

      const created = await service.create(payload);

      if (!show_on_website) {
        await service.hide(created.id);
      }

      try {
        await syncVariantAndPrice({
          productId: created.id,
          options: draftOptions,
          variants: draftVariants,
          priceContext: priceEditContext,
        });
      } catch (syncError) {
        showNotify('Đã tạo sản phẩm nhưng chưa đồng bộ xong biến thể/bảng giá, vui lòng kiểm tra lại.');
      }

      showNotify('Đã tạo sản phẩm');
      navigate(`/product/edit/${created.id}`);
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async () => {
    if (!id) return;
    showLoading(true);
    try {
      await service.delete(id);
      showNotify('Đã xoá sản phẩm');
      navigate('/products');
    } finally {
      showLoading(false);
    }
  };

  const copyProduct = async () => {
    if (!id) return;
    navigate(`/product/add?copy=${encodeURIComponent(id)}`);
  };

  const hideProduct = async () => {
    if (!id) return;
    showLoading(true);
    try {
      await service.hide(id);
      showNotify('Đã ẩn sản phẩm');
      await loadData();
    } finally {
      showLoading(false);
    }
  };

  const unhideProduct = async () => {
    if (!id) return;
    showLoading(true);
    try {
      await service.unhide(id);
      showNotify('Đã hiển thị sản phẩm');
      await loadData();
    } finally {
      showLoading(false);
    }
  };

  const setOutOfStock = async (nextValue) => {
    if (!id) return;
    showLoading(true);
    try {
      await service.outOfStock(id, nextValue);
      showNotify(nextValue ? 'Đã chuyển sang hết hàng' : 'Đã chuyển sang còn hàng');
      await loadData();
    } finally {
      showLoading(false);
    }
  };

  const goToVariantEditor = () => {
    if (!id) return;
    navigate(`/product/variant/${id}`);
  };

  const goToPriceEditor = () => {
    if (!id) return;
    navigate(`/product/price/${id}`);
  };

  const summary = useMemo(() => {
    const variants = id ? (data?.variants || []) : (draftVariants || []);
    const totalOnHand = variants.reduce((sum, v) => sum + (Number(v?.on_hand_quantity ?? 0) || 0), 0);
    const totalReserved = variants.reduce((sum, v) => sum + (Number(v?.reserved_quantity ?? 0) || 0), 0);
    return {
      variantCount: variants.length,
      imageCount: (watchedImages || []).length,
      isHidden: !!data?.hide,
      isOutOfStock: !!data?.out_of_stock,
      trackInventory: !!watchedTrackInventory,
      totalOnHand,
      totalReserved,
      totalAvailable: totalOnHand - totalReserved,
    };
  }, [id, data, draftVariants, watchedImages, watchedTrackInventory]);

  const inventoryRows = useMemo(() => {
    const variants = draftVariants || [];
    return variants.map((v) => {
      const onHand = Number(v?.on_hand_quantity ?? 0) || 0;
      const reserved = Number(v?.reserved_quantity ?? 0) || 0;
      return {
        id: v?.id || v?.variant_id,
        variant_title: v?.variant_title || v?.title || 'Mặc định',
        on_hand_quantity: onHand,
        reserved_quantity: reserved,
        available_quantity: Number(v?.available_quantity ?? (onHand - reserved)) || 0,
      };
    });
  }, [id, data, draftVariants]);

  const onInventoryChange = (variantId, onHandQty) => {
    setDraftVariants((prev) => prev.map((v) =>
      (v?.variant_id === variantId || v?.id === variantId)
        ? { ...v, on_hand_quantity: Number(onHandQty) || 0 }
        : v
    ));
  };

  const onInventoryChangeAll = (onHandQty) => {
    setDraftVariants((prev) => prev.map((v) => ({ ...v, on_hand_quantity: Number(onHandQty) || 0 })));
  };

  const onDraftVariantStateChange = ({ options, variants }) => {
    if (options) {
      setDraftOptions(cloneDraftOptions(options));
    }
    if (variants) {
      setDraftVariants(cloneDraftVariants(variants));
    }
  };

  const onDraftPriceChange = (variants) => {
    setDraftVariants(cloneDraftVariants(variants));
  };

  const onVariantStateChange = ({ options, variants }) => {
    onDraftVariantStateChange({ options, variants });
  };

  const onPriceStateChange = (payload) => {
    if (Array.isArray(payload)) {
      onDraftPriceChange(payload);
      return;
    }

    const nextVariants = payload?.variants || [];
    onDraftPriceChange(nextVariants);
    setPriceEditContext({
      selectedSourceKey: payload?.selectedSourceKey || 'base',
      isTableSource: !!payload?.isTableSource,
      isTableManualMode: !!payload?.isTableManualMode,
      isTableAutoMode: !!payload?.isTableAutoMode,
      selectedTableId: payload?.selectedTable?.id || null,
    });
  };

  return {
    id,
    form,
    data,
    draftOptions,
    draftVariants,
    summary,
    inventoryRows,
    saving,
    showAdvanced,
    setShowAdvanced,
    onFinish,
    deleteProduct,
    copyProduct,
    hideProduct,
    unhideProduct,
    setOutOfStock,
    goToVariantEditor,
    goToPriceEditor,
    onInventoryChange,
    onInventoryChangeAll,
    onDraftVariantStateChange,
    onDraftPriceChange,
    onVariantStateChange,
    onPriceStateChange,
    navigate,
  };
};

export default useEditor;
